<template>
	<div class="quick-options-container">
		<div
			v-for="option in options"
			:key="option.key"
			class="slider"
			:aria-label="option.label"
			role="slider"
			aria-valuemin="0"
			aria-valuemax="100"
			:aria-valuenow="Math.round(values[option.key] * 100)"
			@pointerdown="(evt) => startDrag(evt, option.key)"
			@pointermove="(evt) => drag(evt, option.key)"
			@pointerup="(evt) => endDrag(evt, option.key)"
			@pointercancel="(evt) => endDrag(evt, option.key)"
		>
			<div :class="['fill', { empty: values[option.key] === 0, dragging: dragging === option.key }]" :style="{ height: fillHeight(values[option.key]) }">
				<span class="icon" :style="{ maskImage: `url(${option.icon})`, webkitMaskImage: `url(${option.icon})` }"></span>
			</div>
		</div>
	</div>
</template>

<script>
	import { onUnityMessage, postToUnity } from '../utils/unityBridge';
	import brightnessIcon from './images/brightness.svg';
	import volumeIcon from './images/volume.svg';
	import passthroughIcon from './images/passthrough.svg';

	const STORAGE_KEY = 'quickOptions';
	const SEND_INTERVAL = 50;
	// Width of the fill, it never gets smaller than a circle
	const FILL_SIZE = 69;


	function clamp(value) {
		return Math.min(1, Math.max(0, value));
	}

	export default {
		name: 'QuickOptionsComponent',
		data() {
			return {
				options: [
					{ key: 'brightness', label: 'Brightness', icon: brightnessIcon },
					{ key: 'volume', label: 'Volume', icon: volumeIcon },
					{ key: 'passthrough', label: 'Passthrough opacity', icon: passthroughIcon },
				],
				values: { brightness: 0.4, volume: 0, passthrough: 1 },
				dragging: null,
				lastSent: 0,
				sendTimeout: null,
				unsubscribe: null,
			}
		},
		mounted() {
			try {
				const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
				if (saved) {
					this.applyValues(saved);
				}
			} catch (e) {
				console.error('Error reading quick options from localStorage:', e);
			}

			// Unity knows the real values, they override the saved ones
			this.unsubscribe = onUnityMessage('quickOptions.update', this.applyValues);
			postToUnity('quickOptions.requested');
		},
		unmounted() {
			clearTimeout(this.sendTimeout);
			if (this.unsubscribe) {
				this.unsubscribe();
			}
		},
		methods: {
			fillHeight(value) {
				return `calc(${FILL_SIZE}px + (100% - ${FILL_SIZE}px) * ${value})`;
			},
			applyValues(values) {
				if (!values) {
					return;
				}
				this.options.forEach(({ key }) => {
					// Don't jump while the user is dragging the slider
					if (typeof values[key] === 'number' && this.dragging !== key) {
						this.values[key] = clamp(values[key]);
					}
				});
			},
			valueFromPointer(evt) {
				const rect = evt.currentTarget.getBoundingClientRect();
				const style = getComputedStyle(evt.currentTarget);
				const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
				const travel = rect.height - padding - FILL_SIZE;
				const fromBottom = rect.bottom - parseFloat(style.paddingBottom) - evt.clientY - FILL_SIZE / 2;
				return clamp(fromBottom / travel);
			},
			startDrag(evt, key) {
				evt.currentTarget.setPointerCapture(evt.pointerId);
				this.dragging = key;
				this.setValue(key, this.valueFromPointer(evt));
			},
			drag(evt, key) {
				if (this.dragging === key) {
					this.setValue(key, this.valueFromPointer(evt));
				}
			},
			endDrag(evt, key) {
				if (this.dragging !== key) {
					return;
				}
				this.dragging = null;
				this.sendValues(true);
				localStorage.setItem(STORAGE_KEY, JSON.stringify(this.values));
			},
			setValue(key, value) {
				// Snap to the ends so 0 and 100% are easy to reach
				if (value < 0.02) value = 0;
				if (value > 0.98) value = 1;
				this.values[key] = Math.round(value * 100) / 100;
				this.sendValues(false);
			},
			// Sends at most every SEND_INTERVAL ms while dragging, and always the final value
			sendValues(force) {
				clearTimeout(this.sendTimeout);
				const wait = SEND_INTERVAL - (Date.now() - this.lastSent);
				if (force || wait <= 0) {
					this.lastSent = Date.now();
					postToUnity('quickOptions.changed', { ...this.values });
				} else {
					this.sendTimeout = setTimeout(() => this.sendValues(true), wait);
				}
			},
		},
	}
</script>

<style lang="scss">
	html, body {margin: 0; height: 100%; overflow: hidden}
</style>

<style scoped lang="scss">
	.quick-options-container {
		width: 400px;
		height: 400px;
		background-color: #323437;
		padding: 32px;
		box-sizing: border-box;
		display: flex;
		gap: 17px;
		user-select: none;
		touch-action: none;

		.slider {
			width: 101px;
			height: 336px;
			padding: 16px;
			box-sizing: border-box;
			border-radius: 999px;
			background-color: #222427;
			box-shadow: inset 0px 4px 8px 0px #0000004D;
			display: flex;
			align-items: flex-end;

			&:hover {
				cursor: pointer;
			}

			.fill {
				width: 100%;
				border-radius: 999px;
				background-color: #FFFFFF;
				color: #0C0E11;
				display: flex;
				align-items: flex-end;
				justify-content: center;
				transition: height 200ms ease-out, background-color 200ms ease-out, color 200ms ease-out;

				&.dragging {
					transition: background-color 200ms ease-out, color 200ms ease-out;
				}

				&.empty {
					background-color: #323437;
					color: #FFFFFF;
				}

				.icon {
					width: 32px;
					height: 32px;
					margin-bottom: 18px;
					background-color: currentColor;
					mask-size: 32px 32px;
					-webkit-mask-size: 32px 32px;
				}
			}
		}
	}
</style>
